/**
 * "Not before" (spec §9.5): a task is not yet up for doing until the later of its *Not before* (`startAt`, also
 * what *Not now* sets; `snoozedUntil` was merged into it, UI cleanup U3) and — for soft recurring tasks — its last
 * done plus the minimum gap
 * (`rec.lastDoneAt + rec.minGapDays`). Not-yet tasks stay out of bins, Today and suggestions, sit in the
 * list's "Later" section, and ticking one off asks once. Mirror: `server/drang/core/notyet.py`;
 * both pass `tests/vectors/notyet.json`.
 */
import type { Task } from "./schema";

const DAY_MS = 86_400_000;

function at(v: unknown): number {
  return typeof v === "string" && v ? Date.parse(v) : Number.NaN;
}

/** The moment the task becomes due for doing, when that is after `nowMs`; otherwise null. */
export function notBeforeMs(task: Pick<Task, "typeId" | "props">, nowMs: number): number | null {
  const times: number[] = [];
  const start = at(task.props.startAt);
  if (!Number.isNaN(start)) times.push(start);
  if (task.typeId === "recurring") {
    const gap = task.props["rec.minGapDays"];
    const last = at(task.props["rec.lastDoneAt"]);
    if (typeof gap === "number" && gap > 0 && !Number.isNaN(last)) times.push(last + gap * DAY_MS);
  }
  if (!times.length) return null;
  const latest = Math.max(...times);
  return latest > nowMs ? latest : null;
}

export const isNotYet = (task: Pick<Task, "typeId" | "props">, nowMs: number): boolean =>
  notBeforeMs(task, nowMs) !== null;

/** Midnight at the start of the local day of `d` (the start a repeat's next occurrence gets). */
export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
